/**
 * HeaderFooterInlineEditor — Word-style click-to-edit-in-place header/footer
 * editing. Mounts a rich 3-zone (left/center/right) editor overlay exactly
 * over the clicked page header/footer area, with a small floating toolbar.
 *
 * While the user types, the parent receives debounced live updates so every
 * page's decoration re-renders behind the overlay. Closing (Done, Esc, or
 * clicking outside) persists the config via onClose.
 *
 * Scope semantics (matching Word):
 *   - Editing any page edits the default header/footer…
 *   - …unless "Different first page" is on AND the clicked page is page 1,
 *     in which case the first-page variant is edited.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useEditor, EditorContent, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyleKit } from "@tiptap/extension-text-style";
import Image from "@tiptap/extension-image";
import { useTranslation } from "react-i18next";
import { BsImage } from "react-icons/bs";
import { PageNumberFieldExtension } from "./extensions/PageNumberFieldExtension";
import type {
  HeaderFooterSection,
  ParsedHeaderFooterConfig,
} from "./utils/ooxmlParser/types";
import { emptySection } from "./utils/headerFooterZones";
import "./HeaderFooterInlineEditor.css";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface HeaderFooterInlineEditorProps {
  kind: "header" | "footer";
  pageNumber: number;
  /** The clicked .rm-page-header / .rm-page-footer element */
  targetEl: HTMLElement;
  config: ParsedHeaderFooterConfig;
  /** Debounced live updates while typing (re-renders page decorations) */
  onLiveChange: (config: ParsedHeaderFooterConfig) => void;
  /** Final config when editing ends (persist here) */
  onClose: (config: ParsedHeaderFooterConfig) => void;
  /** Optional GCS upload — when provided, inserted images are uploaded */
  onImageUpload?: (file: File) => Promise<{ src: string; gcsFileName: string }>;
}

const PAGE_FIELD_RE =
  /<span[^>]*data-type="page-number-field"[^>]*>[^<]*<\/span>/g;

const FONT_SIZES = ["8pt", "9pt", "10pt", "11pt", "12pt", "14pt", "16pt"];

// ---------------------------------------------------------------------------
// Mini zone editor (one per zone)
// ---------------------------------------------------------------------------

interface ZoneEditorProps {
  html: string;
  placeholder: string;
  align: "left" | "center" | "right";
  autoFocus?: boolean;
  onChange: (html: string) => void;
  onFocusEditor: (editor: Editor) => void;
  /** Reports the editor instance once created (toolbar targeting) */
  onEditorReady: (editor: Editor) => void;
}

const ZoneEditor: React.FC<ZoneEditorProps> = ({
  html,
  placeholder,
  align,
  autoFocus,
  onChange,
  onFocusEditor,
  onEditorReady,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        codeBlock: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        horizontalRule: false,
        code: false,
        link: { openOnClick: false },
      }),
      TextStyleKit,
      Image.configure({ inline: true, allowBase64: true }),
      PageNumberFieldExtension,
    ],
    immediatelyRender: false,
    content: (html || "").replace(
      /\{page\}/g,
      '<span data-type="page-number-field" class="page-number-field">{page}</span>'
    ),
    editorProps: {
      attributes: {
        class: `hf-zone-editor hf-zone-${align}`,
        "data-placeholder": placeholder,
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML().replace(PAGE_FIELD_RE, "{page}"));
    },
    onFocus: ({ editor: ed }) => {
      onFocusEditor(ed as Editor);
    },
  });

  useEffect(() => {
    if (!editor) return;
    onEditorReady(editor as Editor);
    if (!autoFocus) return;
    // Defer so the overlay has its final position before focusing
    const timer = setTimeout(() => editor.commands.focus("end"), 30);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  if (!editor) return null;
  return <EditorContent editor={editor} className="hf-zone-wrap" />;
};

// ---------------------------------------------------------------------------
// Main overlay
// ---------------------------------------------------------------------------

const HeaderFooterInlineEditor: React.FC<HeaderFooterInlineEditorProps> = ({
  kind,
  pageNumber,
  targetEl,
  config,
  onLiveChange,
  onClose,
  onImageUpload,
}) => {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const focusedEditorRef = useRef<Editor | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Working copy of the whole config (sections mutate as the user types)
  const configRef = useRef<ParsedHeaderFooterConfig>(
    JSON.parse(JSON.stringify(config))
  );
  const [hasTitlePage, setHasTitlePage] = useState(!!config.hasTitlePage);
  // Bump to remount zone editors when the editing target flips
  // (default ⇄ first-page after toggling "Different first page" on page 1)
  const [targetVersion, setTargetVersion] = useState(0);

  const editingFirstPage = hasTitlePage && pageNumber === 1;

  const sectionKey: keyof ParsedHeaderFooterConfig = editingFirstPage
    ? kind === "header"
      ? "firstPageHeader"
      : "firstPageFooter"
    : kind === "header"
      ? "defaultHeader"
      : "defaultFooter";

  const getSection = useCallback((): HeaderFooterSection => {
    const cfg = configRef.current;
    if (!cfg[sectionKey]) {
      (cfg as any)[sectionKey] = emptySection();
    }
    return cfg[sectionKey] as HeaderFooterSection;
  }, [sectionKey]);

  // Capture the (stable) scroll wrapper once. The header/footer decoration in
  // targetEl gets rebuilt/detached by a live flush, but the wrapper persists —
  // so we anchor to it rather than re-deriving it from a possibly-detached el.
  const wrapperRef = useRef<HTMLElement | null>(null);
  if (!wrapperRef.current) {
    const w = targetEl.closest(
      ".tiptap-editor-scroll-wrapper"
    ) as HTMLElement | null;
    if (w) wrapperRef.current = w;
  }

  // Position the overlay exactly over the clicked header/footer area
  const overlayStyle = useMemo<React.CSSProperties>(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return { display: "none" };
    // Re-resolve a LIVE element when the captured targetEl was detached by a
    // decoration rebuild — otherwise the overlay's position collapses to
    // display:none mid-edit (e.g. toggling "Different first page" after typing).
    let el: HTMLElement | null = targetEl.isConnected ? targetEl : null;
    if (!el) {
      const primary =
        kind === "header"
          ? pageNumber === 1
            ? ".rm-first-page-header"
            : ".rm-page-header"
          : pageNumber === 1
            ? ".rm-first-page-footer"
            : ".rm-page-footer";
      const fallback = kind === "header" ? ".rm-page-header" : ".rm-page-footer";
      el =
        (wrapper.querySelector(primary) as HTMLElement | null) ||
        (wrapper.querySelector(fallback) as HTMLElement | null);
    }
    if (!el) return { display: "none" };
    const rect = el.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();
    return {
      top: rect.top - wrapperRect.top + wrapper.scrollTop,
      left: rect.left - wrapperRect.left + wrapper.scrollLeft,
      width: rect.width,
      minHeight: Math.max(rect.height, 46),
    };
    // Position is recomputed on targetVersion bumps and section changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetEl, targetVersion, kind, pageNumber]);

  // Headers sit at the page top — a toolbar ABOVE them lands off-screen
  // (observed at top:-39px on page 1), so headers get the toolbar BELOW
  // the overlay and footers (page bottom) get it ABOVE.
  const toolbarBelow = kind === "header";

  const emitLive = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onLiveChange(JSON.parse(JSON.stringify(configRef.current)));
    }, 150);
  }, [onLiveChange]);

  // Clear any pending live-change timer if the overlay unmounts without
  // going through finish() (tab/doc switch) — it would otherwise fire into
  // an unmounted parent.
  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  const handleZoneChange = useCallback(
    (zone: "left" | "center" | "right", html: string) => {
      const section = getSection();
      section[zone] = html;
      emitLive();
    },
    [getSection, emitLive]
  );

  const finish = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onClose(JSON.parse(JSON.stringify(configRef.current)));
  }, [onClose]);

  // Esc to close, outside click to close
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        finish();
      }
    };
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (rootRef.current && !rootRef.current.contains(target)) {
        finish();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("mousedown", onMouseDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("mousedown", onMouseDown, true);
    };
  }, [finish]);

  // Toolbar actions target the zone editor that currently holds DOM focus
  // (resolved at click time — TipTap focus events proved unreliable for
  // tracking this), falling back to the last reported focus, then the
  // left zone.
  const zoneEditorsRef = useRef<Record<string, Editor | null>>({});
  const withFocused = useCallback((fn: (ed: Editor) => void) => {
    const editors = ["left", "center", "right"]
      .map(zone => zoneEditorsRef.current[zone])
      .filter(Boolean) as Editor[];
    const active = document.activeElement;
    const ed =
      editors.find(e => e.view && e.view.dom === active) ||
      focusedEditorRef.current ||
      editors[0];
    if (ed) fn(ed);
  }, []);

  const handleInsertImageFile = useCallback(
    async (file: File) => {
      const toBase64 = (f: File) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(f);
        });
      let src = await toBase64(file);
      if (onImageUpload) {
        try {
          const uploaded = await onImageUpload(file);
          src = uploaded.src;
        } catch {
          // Keep the base64 preview if upload fails — still renders
        }
      }
      withFocused(ed => ed.chain().focus().setImage({ src }).run());
    },
    [onImageUpload, withFocused]
  );

  const handleTitlePageToggle = useCallback(
    (checked: boolean) => {
      const cfg = configRef.current;
      cfg.hasTitlePage = checked;
      if (checked) {
        if (!cfg.firstPageHeader) {
          cfg.firstPageHeader = emptySection();
        }
        if (!cfg.firstPageFooter) {
          cfg.firstPageFooter = emptySection();
        }
      }
      setHasTitlePage(checked);
      // Editing target may flip between default and first-page sections
      if (pageNumber === 1) setTargetVersion(v => v + 1);
      emitLive();
    },
    [emitLive, pageNumber]
  );

  const section = getSection();
  const zoneKey = (zone: string) => `${sectionKey}-${zone}-${targetVersion}`;
  const zoneProps = (zone: "left" | "center" | "right") => ({
    html: section[zone] || "",
    align: zone,
    onChange: (html: string) => handleZoneChange(zone, html),
    onFocusEditor: (ed: Editor) => {
      focusedEditorRef.current = ed;
    },
    onEditorReady: (ed: Editor) => {
      zoneEditorsRef.current[zone] = ed;
    },
  });

  return (
    <div
      ref={rootRef}
      className={`hf-inline-editor hf-inline-${kind}`}
      style={overlayStyle}
    >
      {/* Floating toolbar */}
      <div
        className={`hf-inline-toolbar ${toolbarBelow ? "below" : "above"}`}
        onMouseDown={e => {
          // Keep zone focus on toolbar clicks — but NOT for the <select>:
          // preventDefault on mousedown stops the native dropdown opening.
          if (!(e.target as HTMLElement).closest("select")) {
            e.preventDefault();
          }
        }}
      >
        <span className="hf-inline-scope">
          {kind === "header"
            ? t("tiptapEditor.headerFooter.header", "Header")
            : t("tiptapEditor.headerFooter.footer", "Footer")}
          {editingFirstPage
            ? ` — ${t("tiptapEditor.headerFooter.firstPageShort", "first page")}`
            : ""}
        </span>
        <span className="hf-inline-divider" />
        <button
          type="button"
          className="hf-inline-btn"
          title={t("tiptapEditor.toolbar.bold", "Bold")}
          onClick={() =>
            withFocused(ed => ed.chain().focus().toggleBold().run())
          }
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          className="hf-inline-btn"
          title={t("tiptapEditor.toolbar.italic", "Italic")}
          onClick={() =>
            withFocused(ed => ed.chain().focus().toggleItalic().run())
          }
        >
          <em>I</em>
        </button>
        <button
          type="button"
          className="hf-inline-btn"
          title={t("tiptapEditor.toolbar.underline", "Underline")}
          onClick={() =>
            withFocused(ed => ed.chain().focus().toggleUnderline().run())
          }
        >
          <u>U</u>
        </button>
        <select
          className="hf-inline-select"
          defaultValue=""
          title={t("tiptapEditor.toolbar.fontSize", "Size")}
          onChange={e => {
            const v = e.target.value;
            if (v) {
              withFocused(ed =>
                (ed.chain().focus() as any).setFontSize(v).run()
              );
            }
          }}
        >
          <option value="">
            {t("tiptapEditor.toolbar.sizeDefault", "Size")}
          </option>
          {FONT_SIZES.map(s => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <span className="hf-inline-divider" />
        <button
          type="button"
          className="hf-inline-btn hf-inline-btn-page"
          title={t(
            "tiptapEditor.headerFooter.insertPageNumber",
            "Insert Page Number"
          )}
          onClick={() =>
            withFocused(ed =>
              (ed.chain().focus() as any).insertPageNumber().run()
            )
          }
        >
          #
        </button>
        <button
          type="button"
          className="hf-inline-btn"
          title={t("tiptapEditor.insertMenu.image", "Image")}
          onClick={() => imageInputRef.current?.click()}
        >
          <BsImage />
        </button>
        <span className="hf-inline-divider" />
        <label className="hf-inline-check">
          <input
            type="checkbox"
            checked={hasTitlePage}
            onChange={e => handleTitlePageToggle(e.target.checked)}
          />
          {t(
            "tiptapEditor.headerFooter.differentFirstPage",
            "Different first page"
          )}
        </label>
        <span className="hf-inline-divider" />
        <button type="button" className="hf-inline-done" onClick={finish}>
          {t("tiptapEditor.headerFooter.done", "Done")}
        </button>
      </div>

      {/* Three editable zones, laid out like the rendered .hfz grid */}
      <div className="hf-inline-zones">
        <ZoneEditor
          key={zoneKey("left")}
          {...zoneProps("left")}
          placeholder={t("tiptapEditor.headerFooter.left", "Left")}
          autoFocus
        />
        <ZoneEditor
          key={zoneKey("center")}
          {...zoneProps("center")}
          placeholder={t("tiptapEditor.headerFooter.center", "Center")}
        />
        <ZoneEditor
          key={zoneKey("right")}
          {...zoneProps("right")}
          placeholder={t("tiptapEditor.headerFooter.right", "Right")}
        />
      </div>

      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) handleInsertImageFile(file);
          e.target.value = "";
        }}
      />
    </div>
  );
};

export default HeaderFooterInlineEditor;
